import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import format from 'string-template';

import useQuickSearch from '../../../hooks/useQuickSearch';
import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import User, { UserRole } from '../../../Model/User';
import { removeUserFromRole } from '../../utils/user';
import QuickSearchToolbar from '../QuickSearchToolbar';
import { PrimaryButton } from '../../../components/Button/ActionButtons';
import ConfirmDialog from '../ConfirmDialog';
import LoadingOverlay from '../LoadingOverlay';
import {
  HeaderRow,
  TitleContainer,
  Title,
} from '../../../components/v2/Header';
import AddUserModal from '../UserAddModal';
import LeadAssignmentModal from '../LeadAssignmentModal';
import {
  Container,
  StyledButton,
  dataGridStyles,
  LeadContainer,
  LeadInfoContainer,
  ButtonGroup,
} from './styles';

import texts from './texts.json';

const UserManagement = ({
  role,
  title,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userCollection, setUserCollection] = useState({ docs: [] });
  const [showUserAddingModal, setShowUserAddingModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForLead, setSelectedUserForLead] = useState(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('assign');
  const [selectedUserForLeadRemoval, setSelectedUserForLeadRemoval] = useState(null);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const initUsers = async () => {
      setIsLoading(true);
      const usersCol = await User.getUsersByRole(role);
      if (isComponentMountedRef.current) {
        setUserCollection(usersCol);
        setIsLoading(false);
      }
    };
    initUsers();
  }, [
    isComponentMountedRef,
    role,
  ]);

  const refreshUserList = useCallback(async () => {
    setIsLoading(true);
    try {
      const usersCol = await User.getUsersByRole(role);
      if (isComponentMountedRef.current) {
        setUserCollection(usersCol);
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [isComponentMountedRef, role]);

  const handleAssignLead = (user) => {
    setSelectedUserForLead(user);
    setModalMode('assign');
    setIsLeadModalOpen(true);
  };

  const handleRemoveLead = (user) => {
    setSelectedUserForLeadRemoval(user);
  };

  const handleUpdateLead = (user) => {
    setSelectedUserForLead(user);
    setModalMode('update');
    setIsLeadModalOpen(true);
  };

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: texts.headers.name,
      flex: 10,
      quickSearch: true,
    },
    {
      field: 'email',
      headerName: texts.headers.email,
      flex: 10,
      quickSearch: true,
    },
    {
      field: 'actions',
      headerName: texts.headers.actions,
      flex: 5,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <PrimaryButton
          onClick={() => setSelectedUser(row)}
          variant="info"
          size="medium"
        >
          {format(texts.removeFromRole, { role })}
        </PrimaryButton>
      ),
    },
    {
      field: 'Lead',
      headerName: 'Lead',
      flex: 15,
      minWidth: 300,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const hasLead = row.leadName && row.leadId;
        return (
          <LeadContainer>
            {hasLead ? (
              <LeadInfoContainer>
                <div>{row.leadName}</div>
                <ButtonGroup>
                  <PrimaryButton
                    variant="info"
                    size="medium"
                    onClick={() => handleUpdateLead(row)}
                  >
                    {texts.updateLead}
                  </PrimaryButton>
                  <PrimaryButton
                    variant="info"
                    size="medium"
                    onClick={() => handleRemoveLead(row)}
                  >
                    {texts.removeLead}
                  </PrimaryButton>
                </ButtonGroup>
              </LeadInfoContainer>
            ) : (
              <PrimaryButton
                variant="info"
                size="medium"
                onClick={() => handleAssignLead(row)}
              >
                {texts.assignLead}
              </PrimaryButton>
            )}
          </LeadContainer>
        );
      },
    },
  ].filter((column) => role === UserRole.AC || column.field !== 'Lead'), [role]);

  const handleRemoveUserFromRole = useCallback(async () => {
    setIsLoading(true);
    try {
      await removeUserFromRole(selectedUser.id, role);
      showToast(format(texts.removeSuccess, { role }));
      const usersCol = await User.getUsersByRole(role);
      if (isComponentMountedRef.current) {
        setUserCollection(usersCol);
      }
    } catch (error) {
      showToast(format(texts.removeError, { role }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        setSelectedUser(null);
      }
    }
  }, [
    selectedUser,
    isComponentMountedRef,
    showToast,
    role,
  ]);

  const handleConfirmLeadRemoval = async () => {
    setIsLoading(true);
    try {
      const userDoc = await User.getById(selectedUserForLeadRemoval.id);
      await userDoc.updateFields({
        leadId: null,
        leadName: null,
      });
      await refreshUserList();
      showToast(texts.leadRemovedSuccessfully);
    } catch (error) {
      showToast(texts.errorRemovingLead, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        setSelectedUserForLeadRemoval(null);
      }
    }
  };

  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(userCollection.docs, columns);

  return (
    <Container>
      <HeaderRow>
        <TitleContainer>
          <Title>{title}</Title>
        </TitleContainer>
        <StyledButton
          variant="contained"
          onClick={() => setShowUserAddingModal(true)}
        >
          {texts.addNewUser}
        </StyledButton>
      </HeaderRow>
      <DataGrid
        sx={dataGridStyles}
        rows={quickSearchRows}
        columns={columns}
        rowHeight={50}
        pagination
        autoPageSize
        components={{
          Toolbar: QuickSearchToolbar,
        }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.searchPlaceholder,
          },
        }}
      />
      <AddUserModal
        isOpen={showUserAddingModal}
        setShowUserAddingModal={setShowUserAddingModal}
        setUserCollection={setUserCollection}
        role={role}
        setIsLoading={setIsLoading}
      />
      <ConfirmDialog
        isOpen={!!selectedUser}
        onCancel={() => setSelectedUser(null)}
        onConfirm={handleRemoveUserFromRole}
        dialogTexts={{
          title: format(texts.removeFromRoleConfirm, { userName: selectedUser?.name, role }),
        }}
      />
      {/* Confirm dialog for lead removal */}
      <ConfirmDialog
        isOpen={!!selectedUserForLeadRemoval}
        onCancel={() => setSelectedUserForLeadRemoval(null)}
        onConfirm={handleConfirmLeadRemoval}
        dialogTexts={{
          title: format(texts.removeLeadConfirm, { userName: selectedUserForLeadRemoval?.name }),
        }}
      />
      <LeadAssignmentModal
        open={isLeadModalOpen}
        onClose={() => setIsLeadModalOpen(false)}
        selectedUser={selectedUserForLead}
        mode={modalMode}
        refreshUserList={refreshUserList}
      />
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

UserManagement.propTypes = {
  role: PropTypes.string.isRequired,
  title: PropTypes.string,
};

UserManagement.defaultProps = {
  title: '',
};

export default compose(
  observer,
)(UserManagement);
